/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";

// @mui icons
import GoogleIcon from "@mui/icons-material/Google";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import AppLoader from "components/AppLoader";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// API Configuration
import appSettings from "appsettings.json";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

function Basic() {
  const navigate = useNavigate();
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState(false);
  const [phoneNo, setPhoneNo] = useState("");
  const [otpStep, setOtpStep] = useState(false);

  const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleSignIn = async () => {
    if (otpStep) {
      const isOtpValid = otp.trim() !== "";
      setOtpError(!isOtpValid);
      if (!isOtpValid) return;

      setError("");
      setLoading(true);

      const verifyUrl = `${appSettings.baseUrl}${
        appSettings.endpoints.getTokenByOtp
      }?otp=${encodeURIComponent(otp)}&phoneNo=${encodeURIComponent(phoneNo)}`;

      try {
        const response = await fetch(verifyUrl, { method: "POST" });
        if (!response.ok) throw new Error("OTP validation failed");

        const data = await response.json();
        localStorage.setItem("token", data.token);
        localStorage.setItem("refreshToken", data.refreshToken);
        const isTocAccepted = data?.isTocAccepted === true;
        localStorage.setItem("isTocAccepted", isTocAccepted ? "true" : "false");
        navigate(isTocAccepted ? "/" : "/authentication/terms-and-conditions");
      } catch (verifyError) {
        console.error("OTP validation error:", verifyError);
        setError("Sorry OTP validation failed");
      } finally {
        setLoading(false);
      }

      return;
    }

    const isEmailValid = email.trim() !== "";
    const isPasswordValid = password.trim() !== "";

    setEmailError(!isEmailValid);
    setPasswordError(!isPasswordValid);

    if (!isEmailValid || !isPasswordValid) return;

    setError("");
    setLoading(true);
    const url = `${appSettings.baseUrl}${
      appSettings.endpoints.loginInitiate
    }?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;

    try {
      const response = await fetch(url, { method: "POST" });
      if (!response.ok) throw new Error("Login failed");

      const data = await response.json();
      setPhoneNo(data?.phoneNo || "");
      setOtpStep(true);
      setOtp("");
    } catch (error) {
      console.error("Authentication error:", error);
      setError("Sorry login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="success"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign in
          </MDTypography>
          <Grid container spacing={3} justifyContent="center" sx={{ mt: 1, mb: 2 }}>
            <Grid item xs={2}>
              <MDTypography component={MuiLink} href="#" variant="body1" color="white">
                <GoogleIcon color="inherit" />
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                value={email}
                error={emailError}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (e.target.value.trim()) setEmailError(false);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type={showPassword ? "text" : "password"}
                label="Password"
                fullWidth
                value={password}
                error={passwordError}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (e.target.value.trim()) setPasswordError(false);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword} edge="end">
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            {otpStep && (
              <>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="OTP"
                    fullWidth
                    value={otp}
                    error={otpError}
                    onChange={(e) => {
                      setOtp(e.target.value);
                      if (e.target.value.trim()) setOtpError(false);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneAndroidIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>
                <MDBox mt={-1} mb={2}>
                  <MDTypography variant="caption" color="warning" fontWeight="regular">
                    OTP is valid for 1 minute.
                  </MDTypography>
                </MDBox>
              </>
            )}
            <MDBox display="flex" justifyContent="space-between" alignItems="center" ml={-1}>
              <MDBox display="flex" alignItems="center">
                <Switch checked={rememberMe} onChange={handleSetRememberMe} />
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  onClick={handleSetRememberMe}
                  sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
                >
                  &nbsp;&nbsp;Remember me
                </MDTypography>
              </MDBox>
              <MDTypography
                component={Link}
                to="/authentication/forgot-password"
                variant="button"
                color="success"
                fontWeight="medium"
                textGradient
              >
                Forgot password?
              </MDTypography>
            </MDBox>
            {error && (
              <MDBox mt={2} textAlign="center">
                <MDTypography variant="button" color="error" fontWeight="medium">
                  {error}
                </MDTypography>
              </MDBox>
            )}
            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                color="success"
                fullWidth
                onClick={handleSignIn}
                disabled={loading}
              >
                {loading ? (
                  <AppLoader size={20} color="currentColor" />
                ) : otpStep ? (
                  "verify otp"
                ) : (
                  "send otp"
                )}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
